import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import toast from 'react-hot-toast'
import { handleApiError, showSuccessToast } from '@/utils/errorHandling'
import {
  Plus,
  Search,
  Layers,
  Edit,
  Trash2,
  ImagePlus,
  X,
} from 'lucide-react'
import { apiClient } from '@/services/api'
import { useIsAdmin } from '@/store/authStore'
import type { CyberCard } from '@/types'
import styles from './Lessons.module.css'
import cardStyles from './CardGlossary.module.css'

const GAME = 'spudnet'

// Normalizes common "share" links (Google Slides, YouTube) into their
// embeddable form, so admins can paste the link they'd naturally copy
// instead of having to know the embed-specific URL format.
function toEmbeddableUrl(url: string): string {
  const slidesMatch = url.match(/docs\.google\.com\/presentation\/d\/([^/]+)/)
  if (slidesMatch) {
    return `https://docs.google.com/presentation/d/${slidesMatch[1]}/embed?start=false&loop=false&delayms=3000`
  }

  const youtubeWatchMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/)
  if (youtubeWatchMatch) {
    return `https://www.youtube.com/embed/${youtubeWatchMatch[1]}`
  }

  return url
}

export function CardGlossary() {
  const isAdmin = useIsAdmin()
  const [cards, setCards] = useState<CyberCard[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [showFormModal, setShowFormModal] = useState(false)
  const [editingCard, setEditingCard] = useState<CyberCard | null>(null)
  const [detailCard, setDetailCard] = useState<CyberCard | null>(null)

  useEffect(() => {
    loadCards()
  }, [])

  const loadCards = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.getCyberCards({ game: GAME })
      setCards(response.data || [])
    } catch (err: any) {
      setError(handleApiError(err, 'load cards', false))
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (card: CyberCard) => {
    if (!confirm(`Delete card "${card.name}"? This cannot be undone.`)) return
    try {
      await apiClient.deleteCyberCard(card.id)
      showSuccessToast('Card deleted successfully!')
      setDetailCard(null)
      await loadCards()
    } catch (err: any) {
      handleApiError(err, 'delete card')
    }
  }

  const categories = [...new Set(cards.map(c => c.category).filter(Boolean))] as string[]

  const filteredCards = cards.filter(c => {
    if (categoryFilter !== 'all' && c.category !== categoryFilter) return false
    const term = searchTerm.toLowerCase()
    if (!term) return true
    return (
      c.name.toLowerCase().includes(term) ||
      c.category?.toLowerCase().includes(term) ||
      c.tags.some(tag => tag.toLowerCase().includes(term))
    )
  })

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <span className={styles.loadingText}>LOADING CARD GLOSSARY</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <p className={styles.errorText}>{error}</p>
          <button onClick={loadCards} className={styles.retryButton}>TRY AGAIN</button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.titleSection}>
            <h1>CARD GLOSSARY</h1>
            <p>Searchable card-effect repository for Enter The Spudnet</p>
          </div>
          {isAdmin && (
            <button
              onClick={() => { setEditingCard(null); setShowFormModal(true) }}
              className={styles.createButton}
            >
              <Plus className="w-4 h-4" />
              <span>NEW CARD</span>
            </button>
          )}
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className={styles.filtersCard}>
        <div className={styles.filtersGrid}>
          <div className={styles.searchWrapper}>
            <Search className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search cards by name, category, or tag..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          {categories.length > 0 && (
            <div className={styles.filterWrapper}>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </motion.div>

      {filteredCards.length === 0 ? (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className={styles.emptyContainer}>
          <div className={styles.emptyIcon}>
            <Layers className="w-12 h-12" />
          </div>
          <h3 className={styles.emptyTitle}>NO CARDS FOUND</h3>
          <p className={styles.emptyText}>
            {searchTerm || categoryFilter !== 'all'
              ? 'Try adjusting your search or filter'
              : 'Get started by adding the first card'}
          </p>
          {!searchTerm && categoryFilter === 'all' && isAdmin && (
            <button onClick={() => { setEditingCard(null); setShowFormModal(true) }} className={styles.emptyButton}>
              <Plus className="w-4 h-4" />
              <span>ADD FIRST CARD</span>
            </button>
          )}
        </motion.div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className={styles.lessonsGrid}>
          {filteredCards.map((card, index) => (
            <motion.div
              key={card.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(0.1 * index, 1) }}
              className={styles.lessonCard}
            >
              <div className={styles.cardContent} onClick={() => setDetailCard(card)} style={{ cursor: 'pointer' }}>
                {card.image_url ? (
                  <div className={styles.courseImageWrapper}>
                    <img src={card.image_url} alt={card.name} className={styles.courseImage} />
                  </div>
                ) : (
                  <div className={styles.courseImagePlaceholder}>
                    <Layers className="w-8 h-8" />
                  </div>
                )}
                <div className={styles.cardHeader}>
                  <div className={styles.cardInfo}>
                    <h3 className={styles.lessonName}>{card.name}</h3>
                    {card.category && (
                      <span className={`${styles.statusPill} ${cardStyles.categoryPill}`}>{card.category}</span>
                    )}
                  </div>
                </div>
                {card.tags.length > 0 && (
                  <div className={cardStyles.tagRow}>
                    {card.tags.map(tag => (
                      <span key={tag} className={cardStyles.tagChip}>{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {detailCard && (
        <CardDetailModal
          card={detailCard}
          isAdmin={isAdmin}
          onClose={() => setDetailCard(null)}
          onEdit={() => { setEditingCard(detailCard); setDetailCard(null); setShowFormModal(true) }}
          onDelete={() => handleDelete(detailCard)}
        />
      )}

      {showFormModal && (
        <CardFormModal
          card={editingCard}
          onClose={() => setShowFormModal(false)}
          onSuccess={() => { setShowFormModal(false); loadCards() }}
        />
      )}
    </div>
  )
}

function CardDetailModal({
  card,
  isAdmin,
  onClose,
  onEdit,
  onDelete,
}: {
  card: CyberCard
  isAdmin: boolean
  onClose: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={cardStyles.detailModalContent} onClick={(e) => e.stopPropagation()}>
        <div className={cardStyles.detailHeader}>
          <h2 className={styles.modalTitle}>{card.name}</h2>
          <button onClick={onClose} className={styles.imageRemoveBtn}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {card.category && (
          <span className={`${styles.statusPill} ${cardStyles.categoryPill}`}>{card.category}</span>
        )}

        {card.embed_url && (
          <div className={cardStyles.embedWrapper}>
            <iframe
              src={toEmbeddableUrl(card.embed_url)}
              title={`${card.name} embed`}
              allow="autoplay; encrypted-media; fullscreen"
              allowFullScreen
            />
          </div>
        )}

        {card.image_url && (
          <img src={card.image_url} alt={card.name} className={cardStyles.detailImage} />
        )}

        {card.explanation && <p className={cardStyles.detailExplanation}>{card.explanation}</p>}

        {card.tags.length > 0 && (
          <div className={cardStyles.tagRow}>
            {card.tags.map(tag => (
              <span key={tag} className={cardStyles.tagChip}>{tag}</span>
            ))}
          </div>
        )}

        {isAdmin && (
          <div className={styles.modalActions}>
            <button onClick={onDelete} className={`${styles.modalButton} ${styles.cancelButton}`}>
              <Trash2 className="w-4 h-4" style={{ marginRight: 6 }} />
              DELETE
            </button>
            <button onClick={onEdit} className={`${styles.modalButton} ${styles.submitButton}`}>
              <Edit className="w-4 h-4" style={{ marginRight: 6 }} />
              EDIT
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function CardFormModal({
  card,
  onClose,
  onSuccess,
}: {
  card: CyberCard | null
  onClose: () => void
  onSuccess: () => void
}) {
  const [formData, setFormData] = useState({
    name: card?.name || '',
    category: card?.category || '',
    tags: card?.tags.join(', ') || '',
    explanation: card?.explanation || '',
    embed_url: card?.embed_url || '',
  })
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(card?.image_url || null)
  const [loading, setLoading] = useState(false)

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setImageFile(file)
      setImagePreview(URL.createObjectURL(file))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      const fd = new FormData()
      fd.append('cyber_card[name]', formData.name)
      fd.append('cyber_card[category]', formData.category)
      fd.append('cyber_card[explanation]', formData.explanation)
      fd.append('cyber_card[embed_url]', formData.embed_url)
      formData.tags.split(',').map(t => t.trim()).filter(Boolean).forEach(tag => {
        fd.append('cyber_card[tags][]', tag)
      })
      if (imageFile) {
        fd.append('cyber_card[image]', imageFile)
      }
      if (card) {
        await apiClient.updateCyberCard(card.id, fd)
        showSuccessToast('Card updated successfully!')
      } else {
        await apiClient.createCyberCard(fd)
        showSuccessToast('Card created successfully!')
      }
      onSuccess()
    } catch (err: any) {
      handleApiError(err, card ? 'update card' : 'create card')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <h2 className={styles.modalTitle}>{card ? 'EDIT CARD' : 'CREATE NEW CARD'}</h2>

        <form onSubmit={handleSubmit} className={styles.modalForm}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Card Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={styles.formInput}
              placeholder="e.g. Phishing Email"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Category</label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className={styles.formInput}
              placeholder="e.g. Social Engineering"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Tags (comma-separated)</label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              className={styles.formInput}
              placeholder="e.g. phishing, email, social engineering"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Explanation</label>
            <textarea
              rows={4}
              value={formData.explanation}
              onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
              className={styles.formTextarea}
              placeholder="Explain what this card does and the cybersecurity concept it teaches..."
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Card Image</label>
            {imagePreview ? (
              <div className={styles.imagePreviewWrap}>
                <img src={imagePreview} alt="Preview" className={styles.imagePreview} />
                <button
                  type="button"
                  onClick={() => { setImageFile(null); setImagePreview(null) }}
                  className={styles.imageRemoveBtn}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className={styles.uploadDropzone}>
                <ImagePlus className={`w-8 h-8 ${styles.uploadIcon}`} />
                <span className={styles.uploadText}>Click to upload image</span>
                <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
              </label>
            )}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Video / Embed URL (optional)</label>
            <input
              type="text"
              value={formData.embed_url}
              onChange={(e) => setFormData({ ...formData, embed_url: e.target.value })}
              className={styles.formInput}
              placeholder="e.g. https://www.youtube.com/embed/..."
            />
            <p className={styles.formHint}>Paste a YouTube or Google Slides link (the normal share/edit link works — it's converted to an embed view automatically)</p>
          </div>

          <div className={styles.modalActions}>
            <button type="button" onClick={onClose} className={`${styles.modalButton} ${styles.cancelButton}`}>
              CANCEL
            </button>
            <button type="submit" disabled={loading} className={`${styles.modalButton} ${styles.submitButton}`}>
              {loading ? 'SAVING...' : card ? 'SAVE CHANGES' : 'CREATE CARD'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
